import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { COLLECTIONS, COLLECTION_SLUGS, getCollection, type CollectionPage } from "@/lib/collections";
import { TrustBar } from "@/components/TrustBar";
import { Reveal } from "@/components/Reveal";
import { ArrowRight } from "lucide-react";
import { useT } from "@/lib/i18n/provider";
import { useLocalizedCollection } from "@/lib/i18n/content";
import { buildFaqPageSchema } from "@/lib/faqSchema";
import { OVA_BUNDLES, bundleBySlug, bundleUrl } from "@/lib/ovaEcosystem";

export const Route = createFileRoute("/collections/$slug")({
  loader: ({ params }) => {
    const collection = getCollection(params.slug);
    if (!collection) throw notFound();
    return { collection };
  },
  head: ({ params }) => {
    const collection = getCollection(params.slug);
    if (!collection) {
      return {
        meta: [
          { title: "Collection not found | TruWe" },
          { name: "robots", content: "noindex" },
        ],
      };
    }
    const url = `https://truwe.in/collections/${collection.slug}`;
    const image = "https://truwe.in/assets/hero-trio.webp";

    const bundleEntities = (collection.bundleSlugs ?? [])
      .map((slug) => bundleBySlug(slug))
      .filter((b): b is NonNullable<typeof b> => Boolean(b));

    const itemList = [
      ...collection.products.map((p, i) => ({
        "@type": "ListItem",
        position: i + 1,
        url: `https://truwe.in/products/${p.handle}`,
        name: p.title,
      })),
      ...bundleEntities.map((b, i) => ({
        "@type": "ListItem",
        position: collection.products.length + i + 1,
        url: bundleUrl(b),
        name: b.name,
      })),
      ...(collection.external
        ? [
            {
              "@type": "ListItem",
              position: collection.products.length + bundleEntities.length + 1,
              url: `https://truwe.in${collection.external.to}`,
              name: collection.external.title,
            },
          ]
        : []),
    ];

    return {
      meta: [
        { title: collection.seoTitle },
        { name: "description", content: collection.seoDescription },
        { property: "og:title", content: collection.seoTitle },
        { property: "og:description", content: collection.seoDescription },
        { property: "og:type", content: "website" },
        { property: "og:url", content: url },
        { property: "og:image", content: image },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:image", content: image },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        ...(collection.faqs.length
          ? [{ type: "application/ld+json", children: JSON.stringify(buildFaqPageSchema({ pageUrl: url, faqs: collection.faqs })) }]
          : []),
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://truwe.in/" },
              { "@type": "ListItem", position: 2, name: "Shop", item: "https://truwe.in/shop" },
              { "@type": "ListItem", position: 3, name: collection.breadcrumbLabel, item: url },
            ],
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "@id": `${url}#collection`,
            url,
            name: collection.h1,
            description: collection.seoDescription,
            isPartOf: { "@type": "WebSite", url: "https://truwe.in/", name: "TruWe" },
            mainEntity: {
              "@type": "ItemList",
              itemListElement: itemList,
            },
          }),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <div className="mx-auto max-w-3xl px-4 py-24 text-center">
      <h1 className="font-display text-3xl font-bold text-brand-deep">Collection not found</h1>
      <p className="mt-4 text-muted-foreground">
        The collection you&apos;re looking for doesn&apos;t exist. Browse the full{" "}
        <Link to="/shop" className="text-brand underline">TruWe formulary</Link>.
      </p>
    </div>
  ),
  errorComponent: ({ reset }) => (
    <div className="mx-auto max-w-3xl px-4 py-24 text-center">
      <h1 className="font-display text-3xl font-bold text-brand-deep">Something went wrong</h1>
      <button onClick={reset} className="mt-6 rounded-full bg-brand px-5 py-2.5 text-sm font-semibold text-white">
        Try again
      </button>
    </div>
  ),
  component: CollectionRoute,
});

function CollectionRoute() {
  const { collection } = Route.useLoaderData();
  return <CollectionView collection={collection} />;
}

function CollectionView({ collection: source }: { collection: CollectionPage }) {
  const t = useT();
  const collection = useLocalizedCollection(source);
  return (
    <div>
      <TrustBar />
      <div className="mx-auto max-w-6xl px-4 py-12 md:px-8 md:py-16">
        {/* Breadcrumb */}
        <nav aria-label="Breadcrumb" className="mb-6 text-xs text-muted-foreground">
          <ol className="flex flex-wrap items-center gap-2">
            <li><Link to="/" className="hover:text-brand">{t("nav.home")}</Link></li>
            <li aria-hidden>/</li>
            <li><Link to="/shop" className="hover:text-brand">{t("nav.shop")}</Link></li>
            <li aria-hidden>/</li>
            <li className="text-foreground">{collection.breadcrumbLabel}</li>
          </ol>
        </nav>

        {/* Header */}
        <header className="mb-12 max-w-3xl">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-brand">{collection.eyebrow}</p>
          <h1 className="mt-2 font-display text-4xl font-bold text-brand-deep md:text-5xl">
            {collection.h1}
          </h1>
          <p className="mt-5 text-base leading-relaxed text-muted-foreground md:text-lg">{collection.intro}</p>
          <p className="mt-4 text-sm leading-relaxed text-muted-foreground md:text-base">{collection.overview}</p>
        </header>

        {/* Products / CTA cards */}
        <Reveal>
          <section aria-labelledby="collection-picks" className="mb-16">
            <h2 id="collection-picks" className="font-display text-2xl font-bold text-brand-deep md:text-3xl">
              {t("collection.recommended")}
            </h2>
            <div className="mt-6 grid gap-6 md:grid-cols-2">
              {collection.products.map((p) => (
                <article key={p.handle} className="rounded-3xl border border-border bg-card/40 p-6 md:p-8">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-brand">{p.tagline}</p>
                  <h3 className="mt-2 font-display text-xl font-bold text-brand-deep md:text-2xl">{p.title}</h3>
                  <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
                    {p.bullets.map((b) => (
                      <li key={b} className="flex gap-2">
                        <span aria-hidden className="mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-brand" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/products/$handle"
                    params={{ handle: p.handle }}
                    className="mt-6 inline-flex items-center gap-1 text-sm font-semibold text-brand hover:underline"
                  >
                    {t("collection.viewFormulation")} <ArrowRight className="h-4 w-4" />
                  </Link>
                </article>
              ))}
              {collection.external && (
                <article className="rounded-3xl border border-border bg-card/40 p-6 md:p-8">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-brand">{t("collection.exploreRange")}</p>
                  <h3 className="mt-2 font-display text-xl font-bold text-brand-deep md:text-2xl">{collection.external.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground">{collection.external.tagline}</p>
                  <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
                    {collection.external.bullets.map((b) => (
                      <li key={b} className="flex gap-2">
                        <span aria-hidden className="mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-brand" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to={collection.external.to}
                    className="mt-6 inline-flex items-center gap-1 text-sm font-semibold text-brand hover:underline"
                  >
                    {t("collection.visit", { name: collection.external.title })} <ArrowRight className="h-4 w-4" />
                  </Link>
                </article>
              )}
            </div>
          </section>
        </Reveal>

        {/* Build your protocol — Ova ecosystem bundles */}
        {(collection.bundleSlugs?.length ?? 0) > 0 && (
          <Reveal>
            <section aria-labelledby="build-your-protocol" className="mb-16">
              <h2
                id="build-your-protocol"
                className="font-display text-2xl font-bold text-brand-deep md:text-3xl"
              >
                Build your protocol
              </h2>
              <p className="mt-3 max-w-2xl text-muted-foreground">
                Ova Balance is the core formula. Each combination adds complementary nutritional
                support around it.
              </p>
              <div className="mt-6 grid gap-6 md:grid-cols-3">
                {OVA_BUNDLES.filter((b) => collection.bundleSlugs?.includes(b.slug)).map((b) => (
                  <article
                    key={b.slug}
                    className="flex flex-col rounded-3xl border border-border bg-card/40 p-6"
                  >
                    <h3 className="font-display text-xl font-bold text-brand-deep">{b.name}</h3>
                    <p className="mt-1 text-xs font-semibold text-brand">{b.supportLine}</p>
                    <p className="mt-3 flex-1 text-sm text-muted-foreground">{b.cardBlurb}</p>
                    <Link
                      to="/products/$handle"
                      params={{ handle: b.slug }}
                      className="mt-6 inline-flex items-center gap-1 text-sm font-semibold text-brand hover:underline"
                    >
                      {b.cta} <ArrowRight className="h-4 w-4" />
                    </Link>
                  </article>
                ))}
              </div>
            </section>
          </Reveal>
        )}

        {/* Selection guide */}
        <Reveal>
          <section aria-labelledby="how-to-choose" className="mb-16 rounded-3xl border border-border bg-background p-6 md:p-10">
            <h2 id="how-to-choose" className="font-display text-2xl font-bold text-brand-deep md:text-3xl">
              {collection.selectionGuide.heading}
            </h2>
            <p className="mt-3 max-w-2xl text-muted-foreground">{collection.selectionGuide.intro}</p>
            <div className="mt-8 grid gap-4 md:grid-cols-2">
              {collection.selectionGuide.items.map((i) => (
                <div key={i.title} className="rounded-2xl border border-border bg-brand-soft/30 p-5">
                  <h3 className="font-display text-base font-semibold text-brand-deep">{i.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground">{i.body}</p>
                </div>
              ))}
            </div>
          </section>
        </Reveal>

        {/* Ingredients strip */}
        {collection.ingredients.length > 0 && (
          <Reveal>
            <section aria-labelledby="ingredients" className="mb-16">
              <h2 id="ingredients" className="font-display text-2xl font-bold text-brand-deep md:text-3xl">
                {t("collection.keyIngredients")}
              </h2>
              <p className="mt-3 max-w-2xl text-muted-foreground">
                {t("collection.ingredientsNote")}
              </p>
              <ul className="mt-6 grid gap-3 sm:grid-cols-2">
                {collection.ingredients.map((ing) => (
                  <li key={ing.anchor}>
                    <Link
                      to="/ingredients"
                      hash={ing.anchor}
                      className="group flex items-start justify-between gap-4 rounded-2xl border border-border bg-background px-5 py-4 transition hover:border-brand hover:shadow-sm"
                    >
                      <div>
                        <p className="text-sm font-semibold text-brand-deep">{ing.label}</p>
                        <p className="mt-1 text-xs text-muted-foreground">{ing.note}</p>
                      </div>
                      <span className="text-xs font-semibold uppercase tracking-wider text-brand group-hover:underline">
                        {t("collection.details")} →
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          </Reveal>
        )}

        {/* Safety */}
        <Reveal>
          <section aria-labelledby="safety" className="mb-16 rounded-3xl border border-amber-200 bg-amber-50/60 p-6 md:p-8">
            <h2 id="safety" className="font-display text-xl font-bold text-brand-deep md:text-2xl">
              {t("collection.safetyHeading")}
            </h2>
            <p className="mt-3 text-sm text-muted-foreground">
              {t("collection.safetyNote")}
            </p>
            <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
              {collection.safety.map((s) => (
                <li key={s} className="flex gap-2">
                  <span aria-hidden className="mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-amber-500" />
                  <span>{s}</span>
                </li>
              ))}
            </ul>
          </section>
        </Reveal>

        {/* Further reading */}
        {collection.articles.length > 0 && (
          <Reveal>
            <section aria-labelledby="reading" className="mb-16">
              <h2 id="reading" className="font-display text-2xl font-bold text-brand-deep md:text-3xl">
                {t("collection.library")}
              </h2>
              <ul className="mt-6 grid gap-3 sm:grid-cols-2">
                {collection.articles.map((a) => (
                  <li key={a.slug}>
                    <a
                      href={`/blog/${a.slug}`}
                      className="block rounded-2xl border border-border bg-background px-5 py-4 transition hover:border-brand"
                    >
                      <p className="text-sm font-semibold text-brand-deep">{a.title}</p>
                      <p className="mt-1 text-xs text-muted-foreground">{a.excerpt}</p>
                    </a>
                  </li>
                ))}
              </ul>
            </section>
          </Reveal>
        )}

        {/* FAQs */}
        <Reveal>
          <section aria-labelledby="faqs" className="mb-16">
            <h2 id="faqs" className="font-display text-2xl font-bold text-brand-deep md:text-3xl">
              {t("collection.faqs")}
            </h2>
            <dl className="mt-8 divide-y divide-border border-y border-border">
              {collection.faqs.map((f) => (
                <div key={f.q} className="py-6">
                  <dt className="font-display text-base font-semibold text-brand-deep md:text-lg">{f.q}</dt>
                  <dd className="mt-2 text-sm leading-relaxed text-muted-foreground md:text-base">{f.a}</dd>
                </div>
              ))}
            </dl>
          </section>
        </Reveal>

        {/* Other collections, internal linking */}
        <Reveal>
          <section aria-labelledby="other-collections" className="mb-8">
            <h2 id="other-collections" className="font-display text-2xl font-bold text-brand-deep md:text-3xl">
              {t("collection.others")}
            </h2>
            <ul className="mt-6 grid gap-3 sm:grid-cols-2 md:grid-cols-3">
              {COLLECTION_SLUGS.filter((s) => s !== collection.slug).map((s) => (
                <li key={s}>
                  <Link
                    to="/collections/$slug"
                    params={{ slug: s }}
                    className="block rounded-2xl border border-border bg-background px-4 py-3 text-sm font-medium text-brand-deep transition hover:border-brand"
                  >
                    {COLLECTIONS[s].breadcrumbLabel} <span className="text-brand">→</span>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        </Reveal>
      </div>
    </div>
  );
}
